import type { RemovalKind } from "@/types"

/*
 * The Archive, and what a removal says before it happens.
 *
 * Two intents, and the copy is the main thing that separates them — underneath
 * they are the same soft write. So each string below is written to answer the
 * question the facilitator is actually asking at that moment:
 *
 *   Archive  "will I be able to find this again?"  — yes, and here is where.
 *   Delete   "will this really go?"                — out of sight, still undoable.
 *
 * Nothing here promises anything is DESTROYED, because nothing is: the app never
 * hard-deletes, and the record outlives the row. But delete is not undone by the
 * app either, and the copy has to carry both halves of that — "still in the
 * database" and "nothing here puts it back" — or the red button is either a
 * bluff or a trap.
 */

/** The destination, as the nav and the loading screens name it. */
export const ARCHIVE_TITLE = "Archive"

/*
 * The heading over each shelf, and what it says underneath.
 *
 * Per shelf, not per page. The two are different decisions — "we stopped working
 * with them in March" against "this was a typo" — and one heading over both made
 * the segmented control look like a filter on a single list rather than the two
 * places they actually are.
 */
export const ARCHIVE_SHELF_TITLE: Record<RemovalKind, string> = {
  archived: "Archived",
  deleted: "Deleted",
}

export const ARCHIVE_SHELF_BLURB: Record<RemovalKind, string> = {
  archived:
    "Customers and sessions you have finished with, taken out of the working lists and kept. Restore one to put it back.",
  deleted:
    "Customers and sessions that should never have existed. The records are still in the database, but nothing here puts them back.",
}

/** The two shelves, as the segmented control labels them. */
export const ARCHIVE_TABS: Record<RemovalKind, string> = {
  archived: "Archived",
  deleted: "Deleted",
}

export const ARCHIVE_CUSTOMERS = "Customers"
export const ARCHIVE_SESSIONS = "Sessions"

export const ARCHIVE_EMPTY: Record<RemovalKind, string> = {
  archived:
    "Nothing archived yet. Archiving a customer or a session files it here and takes it out of the lists.",
  deleted:
    "Nothing deleted. Deleting is for a customer or a session that should never have existed — the record is kept, but the app will not put it back.",
}

export const ARCHIVE_RESTORE = "Restore"

/** Why a session in the Archive has no Restore button of its own. */
export const ARCHIVE_WITH_CUSTOMER = "With the customer"
export function archivedWithCustomer(name: string): string {
  return `Filed with ${name || "its customer"}. Restore them to bring it back.`
}

export const TABLE_REMOVED = "Removed"

/** Under the date, on a row somebody else filed away. */
export function removedByLine(name: string): string {
  return `by ${name}`
}

/* --------------------------------------------------------------- dialogs */

/** What the button says, and what the dialog is called, per intent. */
export const REMOVE_ACTION: Record<RemovalKind, string> = {
  archived: "Archive",
  deleted: "Delete",
}

/**
 * What an icon-only Archive or Delete control is called: its tooltip bubble AND
 * its accessible name, which have to be the same string — `Tooltip` is
 * `aria-hidden`, so the trigger's own label is the only thing announced.
 *
 * It names the thing it acts on. The controls sit in a far-right column or over
 * a tile's corner, away from the name of the row they belong to, and "Delete" on
 * its own is the wrong amount of information to hover a bin icon for.
 */
export function removeActionLabel(kind: RemovalKind, target: string): string {
  return `${REMOVE_ACTION[kind]} ${target}`
}

export const REMOVE_PENDING: Record<RemovalKind, string> = {
  archived: "Archiving…",
  deleted: "Deleting…",
}

export const SESSION_DIALOG_TITLE: Record<RemovalKind, string> = {
  archived: "Archive this session?",
  deleted: "Delete this session?",
}

export const SESSION_DIALOG_BLURB: Record<RemovalKind, string> = {
  archived:
    "It leaves the session lists and the customer's page, and keeps everything it collected — the roster, the pain board, every job and your notes. You will find it in the Archive.",
  deleted:
    "For a session that should not exist: a mistake, a duplicate, a test run. It leaves every list, the share link stops working, and the app cannot put it back — the record stays in the database, but nothing here restores it. Archive it instead if you might want it again.",
}

export const CUSTOMER_DIALOG_TITLE: Record<RemovalKind, string> = {
  archived: "Archive this customer?",
  deleted: "Delete this customer?",
}

export const CUSTOMER_DIALOG_BLURB: Record<RemovalKind, string> = {
  archived:
    "For a customer you no longer work with. They leave the customer list for every facilitator they are shared with, and everything run for them goes to the Archive with them — none of it is deleted.",
  deleted:
    "For a customer that should not exist: a duplicate, a typo, a test. They leave the lists for every facilitator they are shared with, and their sessions go with them. The app cannot put any of it back — archive them instead if you might want them again.",
}

/**
 * The typed confirmation, on deleting a customer and nowhere else.
 *
 * Not on archiving, which is a filing decision anyone can undo in two clicks, and
 * not on a session, which belongs to one customer. A customer deleted here
 * disappears for every facilitator it is shared with — `facilitatorIds` says who
 * may open it and nothing about who owns it, so there is no rule that could
 * reserve the act for its creator. Typing the name is what stands in for one.
 */
export const REMOVE_CONFIRM_LABEL = "Type the customer's name to confirm"

/* ------------------------------------------------------------ what goes */

export function sessionsStranded(filed: number, kind: RemovalKind, other: boolean): string {
  const which = other ? "other " : filed === 1 ? "one " : ""
  const subject = filed === 1 ? `Their ${which}session is` : `Their ${which}${filed} sessions are`
  const stays = filed === 1 ? "stays" : "stay"
  // Subject position in both sentences below, so "they" — not "them".
  const they = filed === 1 ? "it" : "they"
  return kind === "archived"
    ? `${subject} already in the Archive and ${stays} there, but ${they} cannot be restored while the customer is archived — put the customer back first.`
    : `${subject} already in the Archive and ${stays} there, but deleting a customer is not undone, so ${they} can never be restored either.`
}

/**
 * `count` is what TRAVELS — the working sessions. A session filed away on its own
 * does not travel and does not come back when the customer does, so `filed` is
 * counted separately rather than folded in. It also carries the zero case: "no
 * sessions have been run" is false for a customer archived one session at a time.
 */
export function sessionsGoingWith(count: number, kind: RemovalKind, filed = 0): string {
  // `filed` only decides the zero case here. The sentence about those sessions is
  // `sessionsStranded`, rendered as its own callout — see `RemoveDialog.warning`.
  if (count === 0) {
    return filed === 0 ? "No sessions have been run for this customer." : "Nothing goes with them."
  }

  const subject = count === 1 ? "Their 1 session goes" : `Their ${count} sessions go`
  return kind === "archived"
    ? `${subject} to the Archive with them, and ${count === 1 ? "comes" : "come"} back when they do.`
    : `${subject} with them, and the app will not put ${count === 1 ? "it" : "them"} back.`
}

/**
 * The customer tile's session count is the WORKING one — it sits beside a status
 * dot read off the same list, and the page behind it lists the same sessions. So
 * what is already filed is named apart rather than added in, or "0 sessions"
 * would say nothing ever ran here.
 */
export function filedAside(filed: number): string {
  return filed === 1
    ? "1 more session is archived or deleted. It is not in the count beside this, and it does not come back with the customer."
    : `${filed} more sessions are archived or deleted. They are not in the count beside this, and they do not come back with the customer.`
}

/**
 * The line that stops someone emptying a room by accident.
 *
 * A live session USED to be refused outright here. The product asks for the
 * opposite — a session created by mistake has to be disposable while it is
 * running — so the refusal became a count. It says how many people are actually
 * in the room, because "this session is live" is abstract and "4 people are in
 * it" is not.
 *
 * Shown on any session the link still admits people to, which includes one that
 * has not been started — so neither branch may claim the session is RUNNING. A
 * lobby nobody has opened yet still has a working link, and that is the whole
 * fact this line carries when the room is empty.
 */
export function roomWillStop(participants: number): string {
  if (participants === 0)
    return "Nobody is in this session yet, but its share link stops working immediately."
  return participants === 1
    ? "1 person is in this session right now. Their screen will stop, and the share link stops working immediately."
    : `${participants} people are in this session right now. Their screens will stop, and the share link stops working immediately.`
}

/**
 * The opposite warning on a customer, counting rooms rather than people.
 *
 * Named for what it SAYS, not for the control it sits under: removing a customer
 * writes nothing to their sessions, so a room that is running keeps running
 * either way. It was `roomsWillStop`, beside `roomWillStop` above, which is the
 * one sentence in this file that means the reverse of its neighbour.
 *
 * The verb still tracks the intent — a Delete dialog saying "Archiving" would
 * read as the wrong dialog — but the outcome is the same on both shelves.
 */
export function roomsKeepRunning(liveSessions: number, kind: RemovalKind): string {
  if (liveSessions === 0) return ""
  const rooms =
    liveSessions === 1
      ? "One of their sessions is running right now."
      : `${liveSessions} of their sessions are running right now.`
  return `${rooms} ${REMOVE_PENDING_VERB[kind]} the customer does NOT stop the room — the share link keeps working. Delete the session itself to end it.`
}

const REMOVE_PENDING_VERB: Record<RemovalKind, string> = {
  archived: "Archiving",
  deleted: "Deleting",
}

/* ------------------------------------------------------- on the record */

export const ARCHIVED_BADGE: Record<RemovalKind, string> = {
  archived: "Archived",
  deleted: "Deleted",
}

/** The banner on a customer or session opened out of the Archive. */
export const CUSTOMER_BANNER: Record<RemovalKind, string> = {
  archived: "This customer is archived. They are out of the lists until you restore them.",
  deleted:
    "This customer is deleted. The record is still in the database, but the app has no way to put them back.",
}

export const SESSION_GONE_TITLE: Record<RemovalKind, string> = {
  archived: "This session has been archived.",
  deleted: "This session has been deleted.",
}

/**
 * Why an archived session offers no Restore of its own: its customer is filed
 * away too, and a session restored under a removed customer is handed straight
 * back to that removal — it comes back to lists it is still hidden from.
 */
export function sessionHeldByCustomer(kind: RemovalKind, name: string): string {
  const who = name || "Its customer"
  return kind === "archived"
    ? `${who} is archived, so this stays out of the lists whatever happens to it — restore them and it comes back with them.`
    : `${who} is deleted and the app will not put them back, so there is nothing here to restore this into.`
}

/** The way to act on the sentence above. Archived customers only — see `ArchivePage`. */
export const SESSION_GONE_CUSTOMER_LINK = "Open the customer"

export const SESSION_GONE_DETAIL =
  "It is not running any more and nothing can be written into it. Everything it collected is still there."

/** What the room is told, which is not what the facilitator is told. */
export const SESSION_GONE_FOR_GUESTS = "The facilitator has ended this session."
